import * as React from "react";
import { CalendarIcon } from "@radix-ui/react-icons";
import {
  format,
  addQuarters,
  addMonths,
  subMonths,
  endOfMonth,
  startOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subYears,
  subQuarters,
  addYears,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAppStore } from "@/stores";
import { useAPI } from "@/hooks/useAPI";
import { isSyncAvailable } from "@revelate/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDateRangePreset } from "@revelate/utils";

export function CalendarDateRangePicker({
  className,
}: React.HTMLAttributes<HTMLDivElement>) {
  const { dateRange, setDateRange, dateRangeValue, setDateRangeValue, scope } =
    useAppStore((state) => state);
  const { calculateCommissions, isCalculating, setCalculating } = useAPI();
  const [open, setOpen] = React.useState(false);

  // Use a ref to track the last calculated date range to prevent infinite loops
  const lastCalculatedRef = React.useRef<{
    from?: string;
    to?: string;
    scope?: string;
  } | null>(null);

  // Function to trigger calculations when date range changes
  const triggerCalculations = React.useCallback(
    async (newDateRange: typeof dateRange) => {
      if (!newDateRange?.from || !newDateRange?.to) return;
      if (!isSyncAvailable(newDateRange)) return;

      // Safely convert date values to proper Date objects
      let fromDate, toDate;
      
      try {
        fromDate = newDateRange.from instanceof Date ? newDateRange.from : new Date(newDateRange.from);
        toDate = newDateRange.to instanceof Date ? newDateRange.to : new Date(newDateRange.to);
        
        // Create a string representation of the current date range and scope for comparison
        const currentDateRangeKey = {
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
          scope: scope ? `${scope.type}-${scope.value}` : "none",
        };

        // Check if we've already calculated for this exact date range and scope
        if (
          lastCalculatedRef.current &&
          lastCalculatedRef.current.from === currentDateRangeKey.from &&
          lastCalculatedRef.current.to === currentDateRangeKey.to &&
          lastCalculatedRef.current.scope === currentDateRangeKey.scope
        ) {
          return; // Skip calculation if we've already done it for this date range and scope
        }

        // Explicitly set the loading state to true to update the Calculate button
        setCalculating(true);

        // Call the calculation function with showToasts set to false for automatic calculations
        // Pass the scope directly and trigger context for analytics tracking
        const result = await calculateCommissions(
          newDateRange,
          scope || undefined,
          false,
          false,
          { trigger: "date_change" }
        );

        // Only update the last calculated reference if the calculation was successful
        if (result.ok) {
          lastCalculatedRef.current = currentDateRangeKey;
        }
      } catch (error) {
        console.error("Error in date conversion or calculation:", error);
        // Make sure to reset the loading state in case of error
        setCalculating(false);
      }
    },
    [calculateCommissions, setCalculating, scope]
  );

  // Function to check if current date range matches a predefined range
  const getMatchingPresetValue = React.useCallback(() => {
    if (!dateRange?.from || !dateRange?.to) return "";
    // Find matching preset
    const matchingPreset = getDateRangePreset(dateRange);
    return matchingPreset
      ? `${matchingPreset.range.from.toISOString()},${matchingPreset.range.to.toISOString()}`
      : "";
  }, [dateRange]);

  // Update dateRangeValue when URL parameters match a preset
  React.useEffect(() => {
    const matchingValue = getMatchingPresetValue();
    if (matchingValue && !dateRangeValue) {
      setDateRangeValue(matchingValue);
    }
  }, [dateRange, dateRangeValue, getMatchingPresetValue, setDateRangeValue]);

  // Trigger calculations when date range changes
  React.useEffect(() => {
    // Only trigger if we have a complete date range and we're not already calculating
    if (dateRange?.from && dateRange?.to && !isCalculating) {
      // Use a small timeout to avoid triggering during rapid changes
      const timer = setTimeout(() => {
        // This will trigger the calculation and automatically update the loading state
        triggerCalculations(dateRange);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [dateRange, triggerCalculations, isCalculating]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <div className="flex space-x-2">
          <Select
            value={dateRangeValue}
            defaultValue={dateRangeValue}
            onValueChange={setDateRangeValue}
            open={open}
            onOpenChange={setOpen}
          >
            <SelectTrigger>
              <SelectValue placeholder="Custom range" />
            </SelectTrigger>
            <SelectContent position="popper" className="min-h-[25rem]">
              <SelectGroup>
                <SelectLabel className="pl-5">Month</SelectLabel>

                <SelectItem
                  value={`${startOfMonth(
                    addMonths(new Date(), 1)
                  ).toISOString()},${endOfMonth(
                    addMonths(new Date(), 1)
                  ).toISOString()}`}
                >
                  Next month
                </SelectItem>
                <SelectItem
                  value={`${startOfMonth(
                    new Date()
                  ).toISOString()},${endOfMonth(new Date()).toISOString()}`}
                >
                  This month
                </SelectItem>

                <SelectItem
                  value={`${startOfMonth(
                    subMonths(new Date(), 1)
                  ).toISOString()},${endOfMonth(
                    subMonths(new Date(), 1)
                  ).toISOString()}`}
                >
                  Last month
                </SelectItem>
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="pl-5">Quarter</SelectLabel>

                <SelectItem
                  value={`${startOfQuarter(
                    addQuarters(new Date(), 1)
                  ).toISOString()},${endOfQuarter(
                    addQuarters(new Date(), 1)
                  ).toISOString()}`}
                >
                  Next quarter
                </SelectItem>
                <SelectItem
                  value={`${startOfQuarter(
                    new Date()
                  ).toISOString()},${endOfQuarter(new Date()).toISOString()}`}
                >
                  This quarter
                </SelectItem>

                <SelectItem
                  value={`${startOfQuarter(
                    subQuarters(new Date(), 1)
                  ).toISOString()},${endOfQuarter(
                    subQuarters(new Date(), 1)
                  ).toISOString()}`}
                >
                  Last quarter
                </SelectItem>
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="pl-5">Year</SelectLabel>
                <SelectItem
                  value={`${startOfYear(
                    addYears(new Date(), 1)
                  ).toISOString()},${endOfYear(
                    addYears(new Date(), 1)
                  ).toISOString()}`}
                >
                  Next year
                </SelectItem>
                <SelectItem
                  value={`${startOfYear(new Date()).toISOString()},${endOfYear(
                    new Date()
                  ).toISOString()}`}
                >
                  This year
                </SelectItem>
                <SelectItem
                  value={`${startOfYear(
                    subYears(new Date(), 1)
                  ).toISOString()},${endOfYear(
                    subYears(new Date(), 1)
                  ).toISOString()}`}
                >
                  Last year
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          <PopoverTrigger asChild>
            <Button
              id="date"
              variant={"outline"}
              className={cn(
                "w-[280px] justify-start text-left font-normal",
                !dateRange && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange?.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} -{" "}
                    {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(dateRange.from, "LLL dd, y")
                )
              ) : (
                <span>Pick a date</span>
              )}
            </Button>
          </PopoverTrigger>
        </div>

        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={(newDateRange) => {
              setDateRange(newDateRange);
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
